import classNames from 'classnames'
import cn from './index.module.css'
import { FC, useMemo, useState } from 'react'
import TipTapView from '../TipTapView/TipTapView'
import { jsonConvert } from '@/helpers/jsonConvert'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Popover } from 'antd'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { useSelector } from 'react-redux'
import { selectIsAuth } from '@store/profile.reducer'
import { courseEnrollment } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { usePlatformSettingsString } from '@/hooks/usePlatformSettingsString'

interface ITariffCard {
  tariff: ICourseTariff
  className?: string
  onEnroll: () => void
  onDismiss: () => void
}

const TariffCard: FC<ITariffCard> = ({
  tariff,
  className,
  onEnroll,
  onDismiss,
}) => {
  const isAuth = useSelector(selectIsAuth)
  const addAlert = useAddAlert()
  const [isEnrollment, setIsEnrollment] = useState<boolean>(false)
  const { checkSettings } = useGetSettings()
  const showOldPrice = checkSettings('catalog_showOldPrice')
  const freeEnrollText = usePlatformSettingsString(
    'isFreeEnrollButtonTitleChanged',
    'freeEnrollButtonTitle',
    'Записаться',
  )

  const isFree = tariff?.type === 'free'
  const oldPrice =
    tariff?.old_price === null || tariff?.old_price === ''
      ? ''
      : Number(tariff?.old_price)?.toString()

  const description = useMemo(
    () => jsonConvert(tariff?.description),
    [tariff?.description],
  )

  const onEnrollClick = async () => {
    setIsEnrollment(true)
    if (isAuth && tariff?.id && tariff?.type === 'free') {
      const res = await courseEnrollment(tariff?.direction_id, tariff, false)
      if (res.status === 'success') {
        addAlert({
          title: 'Курс приобретен!',
        })
        onDismiss()
      } else {
        addAlert({
          title: 'Возникла ошибка!',
          type: 'error',
        })
      }
    } else {
      onEnroll()
    }
    setIsEnrollment(false)
  }

  const getPriceString = (): string => {
    switch (tariff?.type) {
      case 'free':
        return 'Бесплатно'
      case 'hour':
        return `от ${tariff?.first_price ?? '-'} р/час`
      case 'minute':
        return `от ${tariff?.first_price ?? '-'} р/мин`
      case 'month':
        return `от ${tariff?.first_price ?? '-'} р/мес`
      case 'stream_custom_period':
        return `от ${Math.min(
          ...(tariff?.tariffPeriods?.map((item) => item.price) ?? [0]),
        )} р/мес`
      case 'stream_custom_days':
        return `от ${Math.min(
          ...(tariff?.tariffDays?.map((item) => item?.price) ?? [0]),
        )} р/день`
      case 'stream_custom_weeks':
        return `от ${Math.min(
          ...(tariff?.tariffWeeks?.map((item) => item?.price) ?? [0]),
        )} р/неделя`
      case 'stream_one_time':
        return `${tariff?.first_price ?? '-'} рублей`

      default:
        return ''
    }
  }

  const getOldPriceString = (): string => {
    switch (tariff?.type) {
      case 'hour':
        return `от ${tariff?.old_price ?? '-'} р/час`
      case 'minute':
        return `от ${tariff?.old_price ?? '-'} р/мин`
      case 'month':
        return `от ${tariff?.old_price ?? '-'} р/мес`
      case 'stream_custom_period':
        return `от ${tariff?.old_price ?? '-'} р/мес`
      case 'stream_custom_days':
        return `от ${tariff?.old_price ?? '-'} р/день`
      case 'stream_custom_weeks':
        return `от ${tariff?.old_price ?? '-'} р/неделя`
      case 'stream_one_time':
        return `${tariff?.first_price ?? '-'} рублей`

      default:
        return ''
    }
  }

  return (
    <div className={classNames(cn.wrapper, className)}>
      <div className={cn.content}>
        <div className={cn.title}>{tariff?.name}</div>
        <TipTapView className={cn.tiptap} value={description} />
        <div className={cn.pricing}>
          <div className={cn.currentPrice}>
            {isFree ? 'Бесплатно' : getPriceString()}
          </div>
          {showOldPrice && !isFree && oldPrice && (
            <div className={cn.oldPrice}>{getOldPriceString()}</div>
          )}
        </div>
      </div>
      <div className={cn.footer}>
        {(tariff as any)?.has_demo && (
          <button className={cn.buttonDemo}>
            Демо-доступ
            <Popover overlayClassName={cn.popover} title={`Демо-доступ`}>
              <InfoCircleOutlined
                className={cn.tooltipIcon}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </Popover>
          </button>
        )}
        <button
          className={cn.buttonJoin}
          onClick={onEnrollClick}
          disabled={isEnrollment}
        >
          {isFree ? freeEnrollText : 'Купить'}
        </button>
      </div>
    </div>
  )
}

export default TariffCard
